import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  message,
  Popconfirm,
  Modal,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ClearOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as logService from '../services/logService';

const { RangePicker } = DatePicker;

const OperationLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<logService.OperationLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // 搜索条件
  const [searchUsername, setSearchUsername] = useState('');
  const [searchModule, setSearchModule] = useState('');
  const [searchAction, setSearchAction] = useState('');
  const [searchStatus, setSearchStatus] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<logService.OperationLog | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await logService.listOperationLogs({
        page,
        pageSize,
        username: searchUsername || undefined,
        module: searchModule || undefined,
        action: searchAction || undefined,
        status: searchStatus || undefined,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    loadData();
  };

  const handleReset = () => {
    setSearchUsername('');
    setSearchModule('');
    setSearchAction('');
    setSearchStatus('');
    setDateRange(null);
    setPage(1);
    loadData();
  };

  const handleDelete = async () => {
    if (selectedKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }
    try {
      await logService.deleteOperationLogs(selectedKeys);
      message.success('删除成功');
      setSelectedKeys([]);
      loadData();
    } catch {
      message.error('删除失败');
    }
  };

  const handleClear = async () => {
    try {
      await logService.clearOperationLogs();
      message.success('清空成功');
      loadData();
    } catch {
      message.error('清空失败');
    }
  };

  const handleViewDetail = (record: logService.OperationLog) => {
    setCurrentLog(record);
    setDetailVisible(true);
  };

  // 格式化请求参数
  const formatRequestParams = (params: string | undefined): string => {
    if (!params) return '-';
    
    // 尝试解析为 JSON
    try {
      const parsed = JSON.parse(params);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // 如果不是 JSON，尝试解析为查询字符串
      if (params.includes('=')) {
        try {
          const searchParams = new URLSearchParams(params);
          const obj: Record<string, string> = {};
          searchParams.forEach((value, key) => {
            obj[key] = value;
          });
          return JSON.stringify(obj, null, 2);
        } catch {
          return params;
        }
      }
      return params;
    }
  };

  // 格式化响应数据
  const formatResponseData = (data: string | undefined): string => {
    if (!data) return '-';
    
    try {
      const parsed = JSON.parse(data);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return data;
    }
  };

  // 方法颜色映射
  const methodColorMap: Record<string, string> = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    DELETE: 'red',
    PATCH: 'purple',
  };

  const columns: ColumnsType<logService.OperationLog> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 100,
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 100,
      render: (module) => module || '-',
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => action || '-',
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method) => (
        <Tag color={methodColorMap[method] || 'default'}>{method}</Tag>
      ),
    },
    {
      title: '请求URL',
      dataIndex: 'url',
      key: 'url',
      width: 200,
      ellipsis: true,
      render: (url) => (
        <Tooltip title={url}>
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{url}</span>
        </Tooltip>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration) => (
        <span style={{ color: duration > 1000 ? 'red' : duration > 500 ? 'orange' : 'green' }}>
          {duration}ms
        </span>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="操作日志">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="用户名"
            value={searchUsername}
            onChange={(e) => setSearchUsername(e.target.value)}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            placeholder="模块"
            value={searchModule}
            onChange={(e) => setSearchModule(e.target.value)}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            placeholder="操作"
            value={searchAction}
            onChange={(e) => setSearchAction(e.target.value)}
            style={{ width: 120 }}
            allowClear
          />
          <Select
            placeholder="状态"
            value={searchStatus || undefined}
            onChange={setSearchStatus}
            style={{ width: 100 }}
            allowClear
          >
            <Select.Option value="1">成功</Select.Option>
            <Select.Option value="0">失败</Select.Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
        </Space>

        <Space style={{ marginBottom: 16 }}>
          <Popconfirm
            title={`确定删除选中的 ${selectedKeys.length} 条记录？`}
            onConfirm={handleDelete}
            disabled={selectedKeys.length === 0}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedKeys.length === 0}
            >
              批量删除
            </Button>
          </Popconfirm>
          <Popconfirm title="确定清空所有操作日志？此操作不可恢复！" onConfirm={handleClear}>
            <Button danger icon={<ClearOutlined />}>
              清空日志
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            刷新
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys as string[]),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="操作日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>}
        width={800}
      >
        {currentLog && (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="用户名">{currentLog.username}</Descriptions.Item>
              <Descriptions.Item label="IP">{currentLog.ip}</Descriptions.Item>
              <Descriptions.Item label="模块">{currentLog.module || '-'}</Descriptions.Item>
              <Descriptions.Item label="操作">{currentLog.action || '-'}</Descriptions.Item>
              <Descriptions.Item label="方法">
                <Tag color={methodColorMap[currentLog.method] || 'default'}>
                  {currentLog.method}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={currentLog.status === 1 ? 'green' : 'red'}>
                  {currentLog.status === 1 ? '成功' : '失败'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="耗时">{currentLog.duration}ms</Descriptions.Item>
              <Descriptions.Item label="操作时间">
                {dayjs(currentLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="请求URL" span={2}>
                <code style={{ wordBreak: 'break-all' }}>{currentLog.url}</code>
              </Descriptions.Item>
              {currentLog.errorMsg && (
                <Descriptions.Item label="错误信息" span={2}>
                  <span style={{ color: 'red' }}>{currentLog.errorMsg}</span>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: '#666' }}>请求参数</div>
              <pre
                style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  border: '1px solid #e8e8e8',
                }}
              >
                {formatRequestParams(currentLog.requestParams)}
              </pre>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: '#666' }}>响应数据</div>
              <pre
                style={{
                  maxHeight: 300,
                  overflow: 'auto',
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  border: '1px solid #e8e8e8',
                }}
              >
                {formatResponseData(currentLog.responseData)}
              </pre>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default OperationLog;
